// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { PropsWithChildren, useState } from 'react';
import { Button, ButtonProps, CircularProgress, SxProps } from '@mui/material';
import { useErrorContext } from '../../contexts/ErrorContext';

type AsyncButtonProps = {
  sx?: SxProps;
  color?: ButtonProps['color'];
  size?: 'small' | 'medium' | 'large';
  variant?: 'contained' | 'text' | 'outlined';
  disabled?: boolean;
  onClick: () => Promise<unknown>;
  enableDefaultPadding?: boolean;
  fullWidth?: boolean;
};

export const AsyncButton: React.FC<PropsWithChildren<AsyncButtonProps>> = ({
  sx,
  color = 'primary',
  size = 'small',
  variant = 'contained',
  children,
  disabled,
  onClick,
  enableDefaultPadding,
  fullWidth,
}) => {
  const [loading, setLoading] = useState(false);
  const errorContext = useErrorContext();
  const clickHandler = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setLoading(true);
    onClick()
      .catch((error: Error) => {
        errorContext.enqueueError(error);
      })
      .finally(() => setLoading(false));
  };

  const buttonSx = {
    position: 'relative',
    paddingTop: enableDefaultPadding ? undefined : 0,
    paddingBottom: enableDefaultPadding ? undefined : 0,
    '& .MuiCircularProgress-root': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: '-8px',
      marginLeft: '-8px',
    },
    '& .button-text': {
      opacity: loading ? 0 : 1,
    },
    ...sx,
  } as SxProps;

  return (
    <Button
      sx={buttonSx}
      color={color}
      size={size}
      fullWidth={fullWidth}
      variant={variant}
      disabled={disabled || loading}
      onClick={clickHandler}
    >
      {loading && <CircularProgress size={16} />}
      <span className="button-text">{children}</span>
    </Button>
  );
};
